import classNames from 'classnames';
import { useRouter } from 'next/router';
import Link from 'next/link';
import React, { useMemo } from 'react';
import { ProductQuery } from 'generated/graphql/types';
import { useTranslate } from '@tolgee/react';
import { mapToNode, removeZeroWidthSpace } from '@lib/utils';
import { ChevronRight } from '@components/icons';
import { CollectionTree, useCollectionTreeZustand } from '@zustand/shopData/collectionTree';
import { useRouterHistory } from '@zustand/routerHistory';
import { useCollections } from '@hooks/useCollections';

export interface BreadcrumbsProps {
  className?: string;
  product?: ProductQuery['product'];
  collection?: { title: string; handle: string; link: string };
}

interface Breadcrumb {
  title: string;
  link: string;
}

const _FORBIDDEN_COLLECTIONS = ['selection', 'neuheiten', 'sale'];

function getNestedPath(
  tree: CollectionTree,
  link: string,
  path: Array<{ handle: string; title: string }> = [],
): Array<{ handle: string; title: string }> | null {
  if (tree.link === link) {
    path.push({ title: tree.title, handle: tree.link });
    return path;
  }

  if (!tree.items) {
    return null;
  }

  for (const subTree of tree.items) {
    const newPath = getNestedPath(subTree, link, path);
    if (newPath) {
      newPath.push({ title: tree.title, handle: tree.link });
      return newPath;
    }
  }

  return null;
}

/**  Returns the breadcrumbs of the current navigation. If a product is shown
 * on a following page, product should be given as prop for a clean title.
 */
export const Breadcrumbs = ({ className, product, collection }: BreadcrumbsProps) => {
  const { t } = useTranslate();
  const router = useRouter();
  const tree = useCollectionTreeZustand();
  const [allCollections] = useCollections();
  const collectionPaths = useMemo(
    () =>
      collection && tree
        ? getNestedPath(tree, collection.link)
            ?.filter((e) => e.title !== '')
            .reverse()
        : [],
    [collection, tree],
  );
  const { history } = useRouterHistory();

  // A function to generate individual breadcrumb objects from the current routerpath.
  function generateBreadcrumbsFromRouterPath() {
    // Routerpath without query params at the end.
    const pathWithoutQueryParams = router.asPath.split('?')[0];

    // The routerpath divided into its' individual pathelements/titles.
    const breadcrumbPathTitles = pathWithoutQueryParams
      .split('/')
      .filter((title) => title.length > 0);

    const breadcrumbsList = [{ link: '/', title: 'home' }];

    if (tree?.items?.length && (product || collection)) {
      let sorted: typeof collectionPaths = [];
      if (product && history[1]?.startsWith('/collections')) {
        const lastPageLink = history[1].split('/')[history[1].split('/').length - 1];
        sorted =
          getNestedPath(tree, lastPageLink)
            ?.filter((e) => e.title !== '')
            .reverse() ?? [];
      } else if (product) {
        sorted =
          getNestedPath(
            tree,
            mapToNode(product.collections)
              .filter((s) => _FORBIDDEN_COLLECTIONS.every((item) => !s.handle.includes(item)))
              .map((collection) => ({
                ...collection,
                position: parseInt(
                  allCollections?.find((c) => c.title === collection.title)?.position?.value ?? '0',
                ),
              }))
              .sort((col1, col2) => col1.position - col2.position)?.[0]?.handle,
          )
            ?.filter((c) => c.title !== '')
            .reverse() ?? [];
      } else if (collection && collectionPaths) {
        sorted = collectionPaths;
      }

      for (const collection of sorted) {
        breadcrumbsList.push({
          link: `/collections/${collection.handle}`,
          title: collection.title,
        });
      }
      return breadcrumbsList;
    }

    let path: string;

    breadcrumbPathTitles.forEach((title) => {
      path = `${path ?? ''}/${title}`;

      const trimmedTitle = title.split('#')[0];
      if (['dog-sport', 'equine-sport', 'boat-sport'].includes(trimmedTitle)) {
        return;
      }
      breadcrumbsList.push({
        link: `${path}`,
        title: trimmedTitle,
      });
    });

    return breadcrumbsList;
  }

  const breadcrumbs: Breadcrumb[] = React.useMemo(
    () => generateBreadcrumbsFromRouterPath(),
    [router.asPath, tree, history],
  );

  return (
    <nav className={classNames('flex text-h-12', className)} aria-label="Breadcrumb">
      <div className="flex flex-row items-center">
        {breadcrumbs.map((breadcrumb, index) => {
          const breadcrumbKey = `breadcrumbs.${
            index === 0
              ? breadcrumb.title
              : breadcrumbs
                  .slice(1, index + 1)
                  .map((e) => e.title)
                  .join('.')
          }`;
          let breadcrumbValue =
            removeZeroWidthSpace(t(breadcrumbKey)) === breadcrumbKey &&
            removeZeroWidthSpace(t(`${breadcrumbKey}.default`)) !== `${breadcrumbKey}.default`
              ? t(`${breadcrumbKey}.default`)
              : t(breadcrumbKey);

          // Remove non width whiteSpaces from the breadcrumb value for comparison.
          breadcrumbValue =
            removeZeroWidthSpace(breadcrumbValue) !== breadcrumbKey
              ? breadcrumbValue
              : breadcrumb.title;

          return (
            <div className="flex flex-row items-center" key={breadcrumb.title + index}>
              <Link href={breadcrumb.link}>
                <a
                  className={classNames(
                    'px-2 -mx-2 py-2 rounded hover:underline uppercase',
                    index !== 0 && 'ml-0',
                  )}
                >
                  {breadcrumbValue}
                </a>
              </Link>
              {index < breadcrumbs.length - 1 && (
                <ChevronRight
                  className="flex-shrink-0 w-5 h-5 pl-2 text-black"
                  aria-hidden="true"
                />
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
};
